import { Loading } from 'antd-mobile-v5'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useAddToCart, useCardOptions, useEcardGoods, useShopCart } from '../../services/hooks'
import CartFooter from '../../components/Cart/CartFooter'
import CartHeader from '../../components/Cart/CartHeader'
import EmptyCart from '../../components/Cart/EmptyCart'
import Goods from '../../components/Cart/Goods'
import LoadingStatus from '../../components/FormTools/Loading'
import {
  freeFirstCardKey,
  freeFirstCardKeys,
  getPostageFromCatalog,
  hasPaidPhysicalItem,
  resolveCartItemNo,
} from '../BuyCards/cardOptionUtils'
import ValueAddedServices from '../../components/ValueAddedServices/ValueAddedServices'
import { getEcards, getRedeemedFreeCards, getUserInfo } from '../../reducers/selectors'

import styles from './Cart.module.css'

const Cart = ({
  cart = [],
  setCart,
  checked = [],
  remove,
  toggleChecked,
  cardOptions,
  setChecked,
  checkedList,
  freeFirstKeys,
}) => {
  return cart.length > 0 ? (
    cart.map((item) => (
      <Goods
        remove={remove}
        checked={!!checked.find((c) => c.ecardId === item.ecardId && c.itemNo === item.itemNo)}
        cardOptions={cardOptions}
        cartItem={item}
        key={item.itemNo + item.ecardId}
        toggleChecked={toggleChecked}
        cart={cart}
        setCart={setCart}
        setChecked={setChecked}
        checkedList={checkedList}
        isFreeFirst={!!freeFirstKeys?.has(freeFirstCardKey(item))}
      />
    ))
  ) : (
    <EmptyCart />
  )
}

const CartPage = () => {
  const { loading: cardOptionsLoading, data: cardOptions } = useCardOptions()
  // 商品目录（含 EXPRESS_FEE 运费商品）：与 CartFooter 共享 cacheKey，无额外请求
  const { data: goodsCatalog } = useEcardGoods()
  const { state } = useLocation()

  const { loading: shopCartLoading, run: loadCart } = useShopCart({
    manual: true,
    onSuccess: (data) => {
      setCart(data)
      setChecked(data)
    },
  })
  const [cart, setCart] = useState([])
  const { run: updateCart } = useAddToCart({
    onSuccess: (data) => {
      setCart(data)
    },
  })
  const [checked, setChecked] = useState([...cart])
  const [valueAdded, setValueAdded] = useState([])
  const { userInfo, ecards, redeemedFreeCards } = useSelector((state) => ({
    userInfo: getUserInfo(state),
    ecards: getEcards(state),
    redeemedFreeCards: getRedeemedFreeCards(state),
  }))

  // 免费首卡数量与「有无付费实体卡」（与服务端归零口径一致）：运费口径（2026-09-16 起、
  // 2026-09-17 收紧）下普通实体卡/券单包邮，只有整单没有付费实体卡的免费首卡单收运费
  // （与 CartFooter 总计、结算弹窗同口径）。
  // 基准是**已勾选**的 checked（= 提交给服务端的明细顺序）而非整车：服务端按提交顺序归零首条，
  // 用整车序会出现「未勾选的行标免费、真正被归零的行标 ¥99」。集合同时下传给各行做「免费」标签
  const freeFirstKeys = freeFirstCardKeys(checked, ecards, redeemedFreeCards)
  const freeFirstCount = freeFirstKeys.size
  const hasPaidPhysical = hasPaidPhysicalItem(checked, ecards, redeemedFreeCards)

  useEffect(() => loadCart(), [loadCart])

  const setInitValueAdded = () => {
    if (state && state.added) setValueAdded(state.added)
  }

  const toggleChecked = (item) => {
    if (checked.find((c) => c.ecardId === item.ecardId && c.itemNo === item.itemNo)) {
      setChecked(checked.filter((c) => c.ecardId !== item.ecardId || c.itemNo !== item.itemNo))
    } else {
      setChecked([item, ...checked])
    }
  }

  const remove = (item) => {
    setChecked(checked.filter((c) => c.ecardId !== item.ecardId || c.itemNo !== item.itemNo))
    const updatedCart = cart.filter((c) => c.ecardId !== item.ecardId || c.itemNo !== item.itemNo)
    setCart(updatedCart)
    updateCart(updatedCart)
  }

  const removeAll = () => {
    if (!cart.length > 0) return
    setChecked([])
    const updatedCart = []
    setCart(updatedCart)
    updateCart(updatedCart)
  }

  const toggleCheckAll = () => {
    if (!cart.length > 0) return
    if (checked.length === cart.length) {
      setChecked([])
    } else {
      setChecked([...cart])
    }
  }

  if (shopCartLoading || cardOptionsLoading || !userInfo) return <LoadingStatus />
  return (
    <div className={styles['cart-wrapper']}>
      <CartHeader removeAll={removeAll} />
      {shopCartLoading ? (
        <Loading />
      ) : (
        <div className={styles['cart-item']}>
          <Cart
            cart={cart}
            setCart={setCart}
            cardOptions={cardOptions}
            checked={checked}
            toggleChecked={toggleChecked}
            remove={remove}
            setChecked={setChecked}
            checkedList={checked}
            freeFirstKeys={freeFirstKeys}
          />
          <div className={styles.postage}>
            邮费：¥{' '}
            {getPostageFromCatalog(
              goodsCatalog,
              checked.map((item) => resolveCartItemNo(goodsCatalog, cardOptions, item)),
              freeFirstCount,
              hasPaidPhysical
            ).toFixed(2)}
          </div>
          <div style={{ padding: '0 16px' }}>
            <ValueAddedServices
              valueAdded={valueAdded}
              setValueAdded={setValueAdded}
              onResult={setInitValueAdded}
              page={'cart'}
            />
          </div>
        </div>
      )}
      <CartFooter
        cardOptions={cardOptions}
        checkedList={checked}
        toggleCheckAll={toggleCheckAll}
        shoppingCart={cart}
        valueAdded={valueAdded}
      />
    </div>
  )
}

export default CartPage
